import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';

import { cn } from '../../utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      // z-[var(--cxy-z-index-auth-overlay)] (10000), not Tailwind's bare z-50: the design system's
      // own scale (@fpsm/cxy-tokens) places ad/notification/chatbot UI well above 50 — e.g.
      // --cxy-z-index-takeover-ad (101), --cxy-z-index-cookie-banner (1000) — so at z-50 this overlay
      // (and the modal content below, which shares the same stacking context) could be hidden behind
      // any of that. auth-overlay/auth-modal are the dedicated tokens for exactly this modal, sitting
      // above everything else in the scale. Both apps that render this Dialog (atlas, atlas-astro,
      // product-plus) already hard-depend on `@fpsm/cxy-tokens/tokens.css` being loaded for this same
      // component's cxy-color-* Tailwind classes, so the variable is guaranteed defined here — unlike
      // page-template modules (ParentLinkHeader, Breadcrumbs, SubeventHeader) consumed by external
      // host pages/widgets where these vars are host-provided and not guaranteed, which is why the fix
      // for header/breadcrumb legibility below is a plain opacity change instead of a z-index one.
      //
      // bg-black/50 (was /80): 80% opacity crushes already-dark chrome (e.g. a navy title bar) to
      // near-solid black and its white text to near-illegible dark gray — visually indistinguishable
      // from a broken page rather than a dimmed one. 50% still reads as a clear dim over light body
      // content while leaving dark chrome legible; z-index can't fix this (a `position: static`
      // header always paints below any `position: fixed` overlay regardless of its z-index).
      'fixed inset-0 z-[var(--cxy-z-index-auth-overlay)] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 overflow-auto',
      className,
    )}
    ref={ref}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ children, className, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay>
      <DialogPrimitive.Content
        className={cn(
          // Width is intrinsic to content, not a per-consumer prop: plus's Authentication renders an
          // InfoSection (data-testid="info-section") alongside the form, needing the wide two-panel
          // max-w-4xl; hideInfoPanel'd (non-plus) consumers render the form alone and want the
          // narrower 506px the non-plus flows were designed for. `has-*` reads that structurally so
          // this works uniformly across every consumer (RR7 atlas, product-plus, atlas-astro) from
          // this one compiled rule — a className passed from atlas-astro's own call site would NOT
          // work, since that app has no Tailwind pipeline of its own to compile it (see its own
          // tailwind.css comment on why dist/style.css is its only source of these utilities).
          'relative md:fixed m-4 mt-28 md:mt-0 md:top-[50%] md:left-[50%] z-[var(--cxy-z-index-auth-modal)] grid w-[calc(100%-32px)] md:max-w-[506px] md:has-[[data-testid=info-section]]:max-w-4xl md:translate-x-[-50%] md:translate-y-[-50%] gap-4 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
          className,
        )}
        onOpenAutoFocus={e => e.preventDefault()}
        ref={ref}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <Cross2Icon className="h-8 w-8 text-white cursor-pointer" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogOverlay>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    ref={ref}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description className={cn('text-sm text-muted-foreground', className)} ref={ref} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
